import { useQuery } from 'react-query';

import { baseApiUrl, fetcher, ServerErrorResponse } from 'data_sources/constants';

import type { ApiCollectionResponse } from 'types/api_response';

/** Related organization shape from lexid/related-orgs API (id/name used by Filters). */
export type RelatedOrganization = {
  id: string;
  name: string;
};

/**
 * Fetches related organizations for a given organization ID (lexid/related-orgs API).
 */
export const getRelatedOrganizations = async (
  organizationId: string,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<ApiCollectionResponse<RelatedOrganization>> => {
  const url = `${baseApiUrl()}/lexid/related-orgs/v1/related-organizations/${organizationId}`;

  try {
    const data: RelatedOrganization[] | ApiCollectionResponse<RelatedOrganization> = await fetcher({
      url,
      options: { signal },
    });

    if (Array.isArray(data)) {
      return {
        content: data,
        count: data.length,
      };
    }

    return data;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

/**
 * Hook to fetch and cache related organizations (drives allOrganizations in Detail Filters when not using demo data).
 */
export default function useRelatedOrganizations(
  organizationId?: string,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  return useQuery<ApiCollectionResponse<RelatedOrganization>, ServerErrorResponse>(
    ['relatedOrganizations', organizationId],
    async ({ signal }) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }
      return getRelatedOrganizations(organizationId, signal, onError);
    },
    {
      enabled: !!organizationId,
      keepPreviousData: true,
      ...options,
    }
  );
}
